/**
 * タブ切り替え機能
 * タブトリガー（li要素）をクリックすると、対応するパネルがフェードイン/フェードアウトで切り替わる
 * 初期表示はC-Design
 * URLパラメータ（?tab=xxx）またはハッシュ（#xxx）でタブを指定可能
 */
/* global URLSearchParams */
export const tabSwitch = () => {
	const triggers = document.querySelectorAll('.js-topics-tab-trigger')
	const panels = document.querySelectorAll('.js-topics-tab-panel')

	if (!triggers.length || !panels.length) {
		return
	}

	// アクティブなパネルを表示する関数
	const showPanel = (targetPanel, skipAnimation = false) => {
		// 現在アクティブなパネルを取得
		const activePanel = Array.from(panels).find((panel) =>
			panel.classList.contains('is-active'),
		)

		// 同じパネルの場合は何もしない
		if (activePanel === targetPanel) {
			return
		}

		if (skipAnimation) {
			// アニメーションなしで即座に切り替え（初期化時など）
			if (activePanel) {
				activePanel.classList.remove('is-active')
				activePanel.style.visibility = 'hidden'
				activePanel.style.opacity = '0'
			}

			targetPanel.style.visibility = 'visible'
			targetPanel.classList.add('is-active')
			targetPanel.style.opacity = '1'
			return
		}

		// 既存のアクティブパネルをフェードアウト
		activePanel.style.opacity = '0'

		// フェードアウト完了後にis-activeを削除し、新しいパネルをフェードイン
		window.setTimeout(() => {
			activePanel.classList.remove('is-active')
			activePanel.style.visibility = 'hidden'

			// 新しいパネルの準備（visibilityとpositionを設定）
			targetPanel.style.visibility = 'visible'
			targetPanel.classList.add('is-active')
			targetPanel.style.opacity = '0'

			// 次のフレームでフェードイン開始
			window.requestAnimationFrame(() => {
				targetPanel.style.opacity = '1'
			})
		}, 300)
	}

	// タブを切り替える関数
	const switchTab = (tabName, skipAnimation = false) => {
		const targetTrigger = document.querySelector(
			`.js-topics-tab-trigger[data-tab="${tabName}"]`,
		)
		const targetPanel = document.querySelector(
			`.js-topics-tab-panel[data-panel="${tabName}"]`,
		)

		if (!targetTrigger || !targetPanel) {
			return false
		}

		// すべてのトリガーからis-activeを削除
		triggers.forEach((t) => t.classList.remove('is-active'))

		// 指定されたトリガーにis-activeを追加
		targetTrigger.classList.add('is-active')

		// 対応するパネルを表示
		showPanel(targetPanel, skipAnimation)

		return true
	}

	// URLパラメータまたはハッシュからタブを取得
	const getTabFromURL = () => {
		// URLパラメータから取得（?tab=xxx）
		const urlParams = new URLSearchParams(window.location.search)
		const tabParam = urlParams.get('tab')

		if (tabParam) {
			return tabParam
		}

		// ハッシュから取得（#xxx または #xxx?tab=yyy の形式に対応）
		const hash = window.location.hash.replace('#', '')
		if (hash) {
			// ハッシュ内にクエリパラメータが含まれている場合（#design?tab=t-design）
			const hashParts = hash.split('?')
			if (hashParts.length > 1) {
				const hashParams = new URLSearchParams(hashParts[1])
				const hashTabParam = hashParams.get('tab')
				if (hashTabParam) {
					return hashTabParam
				}
			}
			// ハッシュ自体がタブ名の場合
			return hash
		}

		return null
	}

	// ハッシュからアンカーIDを取得（タブ名以外のハッシュを取得）
	const getAnchorFromURL = () => {
		const hash = window.location.hash.replace('#', '')
		if (!hash) {
			return null
		}

		// ハッシュ内にクエリパラメータが含まれている場合（#design?tab=t-design）
		const hashParts = hash.split('?')
		if (hashParts.length > 1) {
			// クエリパラメータの前の部分がアンカーID
			return hashParts[0] || null
		}

		// ハッシュがタブ名でない場合（タブ名は c-design, r-design, t-design）
		const tabNames = ['c-design', 'r-design', 't-design']
		if (!tabNames.includes(hash)) {
			return hash
		}

		return null
	}

	// アンカー位置までスクロールする関数
	const scrollToAnchor = (anchorId) => {
		if (!anchorId) {
			return
		}

		const targetElement = document.getElementById(anchorId)
		if (!targetElement) {
			return
		}

		const header = document.querySelector('.l-header')
		const headerHeight = header ? header.offsetHeight : 0

		// ターゲット要素の位置を取得
		const targetPosition =
			targetElement.getBoundingClientRect().top + window.pageYOffset

		// ヘッダーの高さ分を引いた位置にスクロール
		const scrollPosition = targetPosition - headerHeight

		// スムーススクロール
		window.scrollTo({
			top: scrollPosition,
			behavior: 'smooth',
		})
	}

	// 初期化時にURLパラメータまたはハッシュからタブを開く
	const initialTab = getTabFromURL()
	const initialAnchor = getAnchorFromURL()

	if (initialTab) {
		// URLから指定されたタブがあれば、アニメーションなしで開く
		switchTab(initialTab, true)

		// タブ切り替え後にアンカー位置までスクロール
		if (initialAnchor) {
			// 少し遅延させてからスクロール（DOM更新を待つ）
			window.setTimeout(() => {
				scrollToAnchor(initialAnchor)
			}, 100)
		}
	} else if (initialAnchor) {
		// タブが指定されていないがアンカーがある場合
		window.setTimeout(() => {
			scrollToAnchor(initialAnchor)
		}, 100)
	}

	// トリガー（li要素）のクリックイベント
	triggers.forEach((trigger) => {
		trigger.addEventListener('click', () => {
			const targetTab = trigger.getAttribute('data-tab')
			switchTab(targetTab, false)
		})
	})
}
